'use client';

import { AuiContainer, FlexTemplate, space } from '@/lib/mx-asurion-ui-react';
import {
  getStepConfig,
  getStepTotals,
  PresentationKey,
} from '@/utils/testConfig';
import { getPaths } from '@/utils/urlPaths';
import { clsx } from 'clsx';
import { usePathname } from 'next/navigation';
import { useMemo } from 'react';

export function Heading({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <FlexTemplate.Heading
      className={clsx(
        className,
        '!px-4',
        'min-[486px]:!px-0',
        '!text-start',
        'box-border',
        'first:!text-wrap',
      )}
    >
      {children}
    </FlexTemplate.Heading>
  );
}

export function SubHeading({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <FlexTemplate.Subheading
      className={clsx(
        className,
        '!px-4',
        'min-[486px]:!px-0',
        '!text-start',
        'box-border',
      )}
    >
      {children}
    </FlexTemplate.Subheading>
  );
}

export function PageTitleContainer({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <FlexTemplate.PageTitleContainer className={clsx(className, 'box-border')}>
      {children}
    </FlexTemplate.PageTitleContainer>
  );
}

export function MainContentContainer({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <FlexTemplate.MainContentContainer
      className={clsx('!overflow-y-visible', className)}
    >
      {children}
    </FlexTemplate.MainContentContainer>
  );
}

export function MainPanelContainer({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <FlexTemplate.MainPanelContainer className={className}>
      {children}
    </FlexTemplate.MainPanelContainer>
  );
}

export function SidePanelContainer({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <FlexTemplate.SidePanelContainer
      className={clsx('main-sidepanel-wrapper', className)}
    >
      <AuiContainer gap={space[600]} className="h-full">
        {children}
      </AuiContainer>
    </FlexTemplate.SidePanelContainer>
  );
}

export function ButtonGroupContainer({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <FlexTemplate.ButtonGroup
      alignment="start"
      className={clsx('buttonGroup', className)}
    >
      {children}
    </FlexTemplate.ButtonGroup>
  );
}

export function ProgressStepBar() {
  const pathName = usePathname();
  const paths = getPaths(pathName);
  const steps = getStepConfig({
    product: paths?.product ?? '',
    partner: paths?.partner ?? '',
    presentation: (paths?.presentation as PresentationKey) ?? 'app',
  });

  const stepTotals = useMemo(() => {
    return getStepTotals({
      currentStep: paths?.step ?? '',
      steps: steps ?? {},
      requiredOnly: true,
    });
  }, [paths?.step, steps]);
  if (steps === undefined) {
    return null;
  }
  const { completeTotal, incompleteTotal, currentStepIndex } = stepTotals;
  if (currentStepIndex < 0) {
    return null;
  }

  // create arrays
  const completedSteps = new Array(completeTotal).fill({
    state: 'complete',
  });
  const inProgressStep = [{ state: 'inprogress' }];
  const incompleteSteps = new Array(incompleteTotal).fill({
    state: 'incomplete',
  });

  return (
    <FlexTemplate.ProgressStepperContainer>
      <FlexTemplate.ProgressStepper
        orientation="horizontal"
        size="small"
        steps={[...completedSteps, ...inProgressStep, ...incompleteSteps]}
        className="progressStepperElement"
      />
    </FlexTemplate.ProgressStepperContainer>
  );
}
